import { useEffect } from 'react';

import { RelayEnvironmentProvider } from 'react-relay';

import { useSetColorMode } from '@atlaskit/app-provider';
import { IsFhsEnabledProvider } from '@atlaskit/navigation-system/fhs-rollout';
import { Root } from '@atlaskit/navigation-system/layout/root';
import { ThemeColorModes } from '@atlaskit/tokens';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';

import { useInitializeRovoChatState } from 'src/hooks/use-initialize-rovo-chat-state';
import { getCurrentUserDataAttribute } from 'src/utils/get-current-user-data-attribute';

import { Aside } from '../aside';
import { Banner } from '../banner';
import { Main } from '../main';
import { useRovoChatContext } from '../nav-system';
import { Panel } from '../panel';
import { environment } from '../relay';
import { SideNav } from '../side-nav';
import { TopNav } from '../top-nav';
import { useTopNavData } from '../top-nav/hooks';

import { useLayoutVisibility, useInitThemeColor } from './hooks';

// All layout areas MUST be direct children of the Root element. The return from this
// provides the main page layout structure for nav4.
// See docs: https://atlassian.design/components/navigation-system/layout/examples
export const Layout = (): JSX.Element => {
  // Eventually this will be where any logic around layout areas will live:
  // visibility, resizing, skip links, widths, default expand/collapse states
  const visibility = useLayoutVisibility();
  const initColorMode = useInitThemeColor();
  const setColorMode = useSetColorMode();
  const isKeyboardShortcutsEnabled = getCurrentUserDataAttribute<boolean>(
    'isKbdShortcutsEnabled'
  );

  const { setChatState } = useRovoChatContext();
  const { currentWorkspace, defaultWorkspace } = useTopNavData();
  const workspaceUuid = currentWorkspace?.uuid || defaultWorkspace?.uuid || '';

  useEffect(() => {
    // once we have the user's color preference from the intial state (global data) we can
    // set the value using the AppProvider which will handle updating the tokens
    if (initColorMode) {
      setColorMode(initColorMode as ThemeColorModes);
    }
  }, [initColorMode, setColorMode]);

  const { rovoEnabledSitesUserHasAccessTo, updateSitePref } =
    useInitializeRovoChatState({ setChatState, workspaceUuid });

  // ModalContextProvider and Relay are for the side nav
  return (
    <RelayEnvironmentProvider environment={environment}>
      <ModalContextProvider>
        {/* NOTE: FHS has been fully rolled out in BBC. We must keep this provider
        ensures any places that call useIsFhsEnabled() in the GSN component will get the enabled state
        until the platform has completed their rollout and no longer uses this. */}
        <IsFhsEnabledProvider value>
          <Root isSideNavShortcutEnabled={isKeyboardShortcutsEnabled}>
            {visibility.banner && <Banner />}
            <TopNav />
            <SideNav />
            <Main
              rovoEnabledSites={rovoEnabledSitesUserHasAccessTo}
              updateRovoPreferredSite={updateSitePref}
            />
            {visibility.aside && <Aside />}
            {visibility.panel && (
              <Panel
                rovoEnabledSites={rovoEnabledSitesUserHasAccessTo}
                updateRovoPreferredSite={updateSitePref}
              />
            )}
          </Root>
        </IsFhsEnabledProvider>
      </ModalContextProvider>
    </RelayEnvironmentProvider>
  );
};
